// src/utils/appLink.js
//
// Internal navigation should stay inside the single-page app. A plain anchor
// to an in-app path unloads the whole application and re-parses the bundle,
// which is slow, throws away app state, and re-runs every boot-time side
// effect. These helpers pick the router's link for in-app paths and leave a
// real anchor everywhere else, so external URLs, `mailto:`, `tel:`, hash-only
// links and anything opening in a new tab keep working exactly as before.
//
// Both forms render a real `<a>` with an href in the end, so crawlers,
// middle-click and open-in-new-tab are unaffected. The decision of what counts
// as in-app lives in ./internalHref, which is unit tested.

export { isInternalHref, resolveInternalHref };

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { isInternalHref, resolveInternalHref } from './internalHref';

// For components that accept MUI's `component` prop (Typography, Link, Box):
//   <Link {...linkProps(card.link)}>…</Link>
export function linkProps(href, { target, rel } = {}) {
  const to = target === '_blank' ? null : resolveInternalHref(href);
  if (!to) return { component: 'a', href, target, rel };
  return { component: RouterLink, to };
}

// For a plain anchor: <AppLink href={postHref} onClick={…}>…</AppLink>
export function AppLink({ href, target, rel, children, ...rest }) {
  const to = target === '_blank' ? null : resolveInternalHref(href);
  if (!to) {
    return (
      <a href={href} target={target} rel={rel} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <RouterLink to={to} {...rest}>
      {children}
    </RouterLink>
  );
}
